"use client";

import { useMemo } from "react";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Toolbar } from "@/components/ui/toolbar";
import {
  FilterButton,
  type ActiveFilter,
} from "@/components/ui/filter-panel";
import { useListMembers } from "@/lib/api/generated";
import {
  buildProjectFilterFields,
  SELECTOR_TABS,
  type ProjectSelector,
} from "./filters";

interface ProjectsListToolbarProps {
  selector: ProjectSelector;
  onSelectorChange: (selector: ProjectSelector) => void;
  search: string;
  onSearchChange: (value: string) => void;
  activeFilters: ActiveFilter[];
  onFiltersChange: (filters: ActiveFilter[]) => void;
  filterOpen: boolean;
  onFilterOpenChange: (open: boolean) => void;
}

export function ProjectsListToolbar({
  selector,
  onSelectorChange,
  search,
  onSearchChange,
  activeFilters,
  onFiltersChange,
  filterOpen,
  onFilterOpenChange,
}: ProjectsListToolbarProps) {
  const { data: membersData } = useListMembers(undefined);
  const filterFields = useMemo(
    () => buildProjectFilterFields(membersData?.members ?? []),
    [membersData?.members],
  );

  return (
    <Toolbar.Container role="search" aria-label="Filter projects">
      <Tabs
        value={selector}
        onValueChange={(v) => onSelectorChange(v as ProjectSelector)}
        size="default"
      >
        <TabsList aria-label="Project scope">
          {SELECTOR_TABS.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Toolbar.Separator />

      <Toolbar.Search
        value={search}
        onChange={onSearchChange}
        aria-label="Search projects"
        className="flex-1 sm:w-[180px] sm:flex-none"
      />

      <Toolbar.Separator />

      <FilterButton
        fields={filterFields}
        filters={activeFilters}
        onFiltersChange={onFiltersChange}
        open={filterOpen}
        onOpenChange={onFilterOpenChange}
      />
    </Toolbar.Container>
  );
}
